import { createContext, useContext, useId, useMemo } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { cn } from '../../lib/cn'

export interface FieldState {
  id: string
  describedBy: string | undefined
  invalid: boolean
  required: boolean
}

const FieldContext = createContext<FieldState | null>(null)

/**
 * Controls read their id, `aria-describedby` and invalid flag from here, so a Field never
 * has to be wired by hand. Returns `null` outside a Field — every control still works alone.
 */
export const useFieldState = (): FieldState | null => useContext(FieldContext)

export interface LabelProps {
  htmlFor?: string
  children: ReactNode
  required?: boolean
  className?: string
  /** Renders on chrome instead of paper. */
  tone?: 'paper' | 'chrome'
}

/** Uppercase, narrow-width label — the design system's `--t-label` role. */
export const Label = ({
  htmlFor,
  children,
  required = false,
  className,
  tone = 'paper',
}: LabelProps): ReactElement => (
  <label
    htmlFor={htmlFor}
    className={cn(
      't-label block',
      tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute',
      className,
    )}
  >
    {children}
    {required ? (
      <span className="text-brass-deep" aria-hidden="true">
        {' '}
        *
      </span>
    ) : null}
  </label>
)

export interface FieldProps {
  label?: ReactNode
  /** Explains the input before it is used. Disappears once an error takes its place. */
  hint?: ReactNode
  /** States what is wrong and what to do. Never apologises. */
  error?: ReactNode
  required?: boolean
  className?: string
  tone?: 'paper' | 'chrome'
  children: ReactNode
}

/** Label + control + hint/error, with the accessibility wiring already done. */
export const Field = ({
  label,
  hint,
  error,
  required = false,
  className,
  tone = 'paper',
  children,
}: FieldProps): ReactElement => {
  const raw = useId()
  const id = `field-${raw.replace(/:/g, '')}`
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined

  const state = useMemo<FieldState>(
    () => ({
      id,
      describedBy: [errorId, hintId].filter(Boolean).join(' ') || undefined,
      invalid: Boolean(error),
      required,
    }),
    [id, errorId, hintId, error, required],
  )

  return (
    <FieldContext.Provider value={state}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {label ? (
          <Label htmlFor={id} required={required} tone={tone}>
            {label}
          </Label>
        ) : null}
        {children}
        {error ? (
          <p id={errorId} className={cn('t-small text-down-deep')} role="alert">
            {error}
          </p>
        ) : hint ? (
          // ink-faint measures 2.8–3.1:1 on paper and fails AA — it's for decorative icons,
          // disabled state and placeholder attributes, not text meant to be read. A hint
          // explains the control before it's used, so it reads in ink-mute (6.06:1) instead,
          // same as the label above.
          <p
            id={hintId}
            className={cn('t-small', tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-mute')}
          >
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  )
}
